import { useState, useEffect } from 'react';
import { FiUpload, FiImage, FiEdit2, FiCheck, FiX } from 'react-icons/fi';
import { toast } from 'react-hot-toast';
import api from '../../../../api/api';

const LogoTab = () => {
  const [isEditingLogo, setIsEditingLogo] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [logo, setLogo] = useState({
    url: '',
    file: null
  });

  useEffect(() => {
    fetchLogo();
  }, []);

  const fetchLogo = async () => {
    try {
      setIsLoading(true);
      const response = await api.getLogo();
      if (response?.image) {
        setLogo({
          url: response.image,
          file: null
        });
      }
    } catch (error) {
      toast.error('Failed to fetch logo');
      console.error('Error fetching logo:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogoFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file size (2MB limit)
      if (file.size > 2 * 1024 * 1024) {
        toast.error('Logo size should be less than 2MB');
        return;
      }

      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error('Please upload an image file');
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        setLogo({
          url: e.target.result,
          file: file
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveLogo = async () => {
    try {
      if (!logo.file) {
        toast.error('Please select a new logo to upload');
        return;
      }

      // Check if file is PNG with transparency
      console.log('Uploading logo file:', logo.file.type);

      const formData = new FormData();
      formData.append('image', logo.file);
      
      // Don't process or convert the image on the client side
      // Let the server handle it as-is

      await api.updateLogo(formData);
      setIsEditingLogo(false);
      toast.success('Logo updated successfully');
      await fetchLogo(); // Refresh the logo
    } catch (error) {
      toast.error('Failed to update logo');
      console.error('Error updating logo:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="bg-white rounded-xl shadow-sm p-3 sm:p-4 md:p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Site Logo</h3>
          <button
            onClick={() => setIsEditingLogo(!isEditingLogo)}
            className="text-indigo-600 hover:text-indigo-700"
          >
            {isEditingLogo ? <FiX size={24} /> : <FiEdit2 size={20} />}
          </button>
        </div>

        <div className="space-y-4">
          <div className="border rounded-lg p-6 bg-transparent flex justify-center items-center min-h-[200px]">
            {logo.url ? (
              <div className="relative w-full max-w-xl mx-auto">
                <div className="bg-transparent backdrop-blur-sm rounded-lg p-4">
                  <img
                    src={logo.url}
                    alt="Site Logo"
                    className="mx-auto max-w-full max-h-[400px] object-contain"
                  />
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center gap-2 text-gray-400">
                <FiImage className="w-12 h-12" />
                <span className="text-sm">No logo uploaded</span>
              </div>
            )}
          </div>

          {isEditingLogo && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Upload New Logo</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoFileChange}
                  className="w-full border rounded-lg p-2"
                />
                <p className="mt-1 text-sm text-gray-500">
                  Maximum file size: 2MB. Supported formats: PNG, JPG, JPEG.
                </p>
              </div>

              <div className="flex justify-end space-x-2">
                <button
                  onClick={() => {
                    setIsEditingLogo(false);
                    setLogo(prev => ({ ...prev, file: null }));
                  }}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveLogo}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2"
                >
                  <FiCheck className="w-4 h-4" />
                  Save Changes
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LogoTab;



